// components/common/DisputeManager.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  createDispute, 
  getApplicationDisputes, 
  updateDisputeStatus,
  resolveDisputeWithResponse,
  acceptDisputeResponse
} from '../../services/disputeService';
import { getDocumentQueries, replyToDocumentQuery, resolveDocumentQuery } from '../../services/documentQueryService';
import { getQueriesByFilter, searchStandardQueries as searchStandardQueriesLocal, getServiceMetadata } from '../../services/localQueryService.react';
import { 
  uploadFile, 
  generateDisputeFilePath, 
  formatFileSize 
} from '../../services/fileUploadService';
import FileViewer, { FileList } from './FileViewer';

const DisputeManager = ({ 
  applicationId, 
  applicationData, 
  onDisputeUpdate,
  onDisputeCountChange = null, // Callback for dispute count changes
  highlightDisputeId = null, // For navigation from notifications
  showQuestionnaire = false, // Whether to show questionnaire alongside
  onQuestionnaireToggle = null, // Callback to toggle questionnaire
  forceDepartment = null // Optional: force department context (e.g., 'policy')
}) => {
  const { userData: user, userRole } = useAuth();
  const [disputes, setDisputes] = useState([]);
  const [documentQueries, setDocumentQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showResponseForm, setShowResponseForm] = useState(false);

  // Form states
  const [disputeForm, setDisputeForm] = useState({
    title: '', // Optional overall title
    priority: 'medium',
    assignedTo: 'hospital',
    issues: [{ type: '', description: '', priority: 'medium', answerType: 'text', title: '' }] // Each issue has its own title
  });

  const [responseForm, setResponseForm] = useState({});
  const [actionLoading, setActionLoading] = useState(null); // Track which action is loading
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDisputeForDetails, setSelectedDisputeForDetails] = useState(null);
  const [showReRaiseForm, setShowReRaiseForm] = useState(false);
  const [reRaiseComment, setReRaiseComment] = useState('');

  // Predefined queries and autocomplete state
  const [standardQueries, setStandardQueries] = useState([]);
  const [filteredQueries, setFilteredQueries] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [localQueriesLoaded, setLocalQueriesLoaded] = useState(false);
  const autocompleteRef = useRef(null);

  // Test local queries service on component mount
  useEffect(() => {
    try {
      const metadata = getServiceMetadata();
      if (metadata.success && metadata.metadata.totalQueriesLoaded > 0) {
        setLocalQueriesLoaded(true);
        console.log(`✅ Local queries loaded: ${metadata.metadata.totalQueriesLoaded} queries available`);
      } else {
        console.warn('⚠️ Local queries not loaded or empty dataset');
      }
    } catch (error) {
      console.error('❌ Error checking local queries:', error);
    }
  }, []);

  // Helper functions for managing multiple issues
  const addIssue = () => {
    setDisputeForm({
      ...disputeForm,
      issues: [...disputeForm.issues, { type: '', description: '', priority: 'medium', answerType: 'text', title: '' }]
    });
  };

  const removeIssue = (index) => {
    if (disputeForm.issues.length > 1) {
      const newIssues = disputeForm.issues.filter((_, i) => i !== index);
      setDisputeForm({ ...disputeForm, issues: newIssues });
    }
  };

  const updateIssue = (index, field, value) => {
    const newIssues = disputeForm.issues.map((issue, i) => {
      if (i === index) {
        const updatedIssue = { ...issue, [field]: value };
        return updatedIssue;
      }
      return issue;
    });
    setDisputeForm({ ...disputeForm, issues: newIssues });
  };

  // Predefined queries functions
  const searchStandardQueries = (searchTerm) => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setFilteredQueries([]);
      setShowAutocomplete(false);
      return;
    }

    try {
      setAutocompleteLoading(true);
      
      // Use local query service - no async needed since data is already loaded
      const result = searchStandardQueriesLocal(searchTerm, { limit: 10 });

      if (result.success && result.queries) {
        console.log(`Found ${result.queries.length} local queries for "${searchTerm}"`);
        setFilteredQueries(result.queries);
        setShowAutocomplete(result.queries.length > 0);
      } else {
        console.log(`No local queries found for "${searchTerm}"`);
        setFilteredQueries([]);
        setShowAutocomplete(false);
      }
    } catch (error) {
      console.error('Error searching local standard queries:', error);
      setFilteredQueries([]);
      setShowAutocomplete(false);
    } finally {
      setAutocompleteLoading(false);
    }
  };

  const selectStandardQuery = (query, issueIndex = null) => {
    if (issueIndex !== null) {
      // Update specific issue with predefined query
      const newIssues = disputeForm.issues.map((issue, i) => 
        i === issueIndex ? { 
          ...issue, 
          title: query.queryHeader || 'Standard Query',
          description: query.queryDescription || ''
        } : issue
      );
      setDisputeForm({ ...disputeForm, issues: newIssues });
    } else {
      // Fallback: update form title and first issue
      setDisputeForm({
        ...disputeForm,
        title: query.queryHeader || 'Predefined Query',
        issues: disputeForm.issues.map((issue, i) => 
          i === 0 ? { 
            ...issue, 
            title: query.queryHeader || 'Standard Query',
            description: query.queryDescription || ''
          } : issue
        )
      });
    }
    setShowAutocomplete(false);
    setFilteredQueries([]);
  };

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowAutocomplete(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (applicationId) {
      loadDisputes();
      loadDocumentQueries();
    }
  }, [applicationId]);

  // Update dispute count when disputes or document queries change
  useEffect(() => {
    if (onDisputeCountChange) {
      const activeDisputesCount = disputes.filter(dispute => 
        dispute.status && dispute.status !== 'resolved'
      ).length;
      const openDocQueriesCount = documentQueries.filter(q => q.status === 'open').length;
      const totalActiveQueries = activeDisputesCount + openDocQueriesCount;
      onDisputeCountChange(totalActiveQueries);
    }
  }, [disputes, documentQueries, onDisputeCountChange]);

  // Handle highlighting dispute from notification navigation
  useEffect(() => {
    if (highlightDisputeId) {
      // Auto-expand the response form for the highlighted dispute
      const highlightedDispute = disputes.find(d => d.id === highlightDisputeId);
      if (highlightedDispute && highlightedDispute.status === 'raised' && userRole === 'Hospital') {
        setSelectedDispute(highlightedDispute);
        setShowResponseForm(true);
      }
    }
  }, [highlightDisputeId, disputes, userRole]);

  // Don't render if user is not loaded yet
  if (!user) {
    return <div>Loading...</div>;
  }

  const loadDisputes = async () => {
    setLoading(true);
    try {
      const result = await getApplicationDisputes(applicationId);
      if (result.success) {
        setDisputes(result.disputes);
        
        // Calculate active disputes count (disputes that are not resolved)
        const activeDisputesCount = result.disputes.filter(dispute => 
          dispute.status && dispute.status !== 'resolved'
        ).length;
        
        // Call the callback with the active disputes count
        if (onDisputeCountChange) {
          onDisputeCountChange(activeDisputesCount);
        }
      }
    } catch (error) {
      console.error('Error loading disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentQueries = async () => {
    try {
      const result = await getDocumentQueries(applicationId);
      if (result.success) {
        // Convert Firestore timestamps and mark as document queries
        const queriesWithDates = result.queries.map(q => ({
          ...q,
          raisedAt: q.raisedAt?.toDate ? q.raisedAt.toDate() : q.raisedAt,
          resolvedAt: q.resolvedAt?.toDate ? q.resolvedAt.toDate() : q.resolvedAt,
          isDocumentQuery: true, // Mark for identification
          documentKey: q.documentKey, // Preserve for resolution
          // Map to dispute-like structure for unified display
          title: `Document Query: ${q.documentTitle || q.documentKey}`,
          description: q.comment,
          status: q.status === 'open' ? 'raised' : 'resolved',
          priority: 'medium', // Default priority for document queries
          raisedBy: q.raisedBy || {},
          assignedTo: 'hospital',
          // Create a synthetic issue for document queries to work with existing form
          issues: [{
            type: 'document_missing',
            title: 'Provide updated document',
            description: q.comment,
            priority: 'medium',
            answerType: 'text+file' // Document queries need both text reply and file option
          }]
        }));
        setDocumentQueries(queriesWithDates);
      }
    } catch (error) {
      console.error('Error loading document queries:', error);
    }
  };

  // Helper function to format display names for disputes
  const formatDisputeUserDisplay = (raisedBy) => {
    // For internal teams, show team name instead of individual names
    const teamMapping = {
      'medical': 'Medical Team',
      'policy': 'Policy Team', 
      'financial': 'Financial Team',
      'hospital': raisedBy.userName // Hospital users show their actual names
    };

    const displayName = teamMapping[raisedBy.department] || raisedBy.userName;
    return `${displayName}`;
  };

  // Helper function to anonymize user information for disputes
  const getAnonymizedUserInfo = (userRole, user, overrideDepartment = null) => {
    const roleMapping = {
      'Doctor': {
        displayName: 'Medical Team',
        department: 'medical'
      },
      'PolicyWriter': {
        displayName: 'Policy Team',
        department: 'policy'
      },
      'FinancialTeam': {
        displayName: 'Financial Team',
        department: 'financial'
      },
      'Hospital': {
        displayName: user.profile?.name || user.email || 'Hospital Representative',
        department: 'hospital'
      }
    };

    const mapping = roleMapping[userRole] || {
      displayName: 'Internal Team',
      department: 'internal'
    };

    return {
      userId: user.uid, // Keep for system tracking
      userName: mapping.displayName, // Anonymized display name
      department: overrideDepartment || mapping.department,
      role: userRole,
      // Store actual user info in a separate field for internal use only
      _internal: {
        actualName: user.profile?.name || user.email,
        actualEmail: user.email
      }
    };
  };

  const handleCreateDispute = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const disputeData = {
        applicationId,
        raisedBy: getAnonymizedUserInfo(userRole, user, forceDepartment),
        ...disputeForm
      };

      const result = await createDispute(disputeData);
      if (result.success) {
        setShowCreateForm(false);
        setDisputeForm({
          title: '',
          priority: 'medium',
          assignedTo: 'hospital',
          issues: [{ type: '', description: '', priority: 'medium', answerType: 'text', title: '' }]
        });
        loadDisputes();
        onDisputeUpdate && onDisputeUpdate();
      }
    } catch (error) {
      console.error('Error creating dispute:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledgeDispute = async (disputeId) => {
    setActionLoading(`acknowledge-${disputeId}`);
    try {
      const anonymizedUser = getAnonymizedUserInfo(userRole, user, forceDepartment);
      // Accept a dispute that is already resolved, this finalizes and locks further edits
      const result = await acceptDisputeResponse(disputeId, {
        userId: user.uid,
        userName: anonymizedUser.userName,
        department: anonymizedUser.department,
        message: 'Query resolved - Response accepted'
      });

      if (result?.success) {
        loadDisputes();
        onDisputeUpdate && onDisputeUpdate();
      }
    } catch (error) {
      console.error('Error resolving dispute:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReRaiseQuery = async () => {
    if (!selectedDisputeForDetails || !reRaiseComment.trim()) {
      alert('Please provide a comment for re-raising the query.');
      return;
    }

    setActionLoading(`re-raise-${selectedDisputeForDetails.id}`);
    try {
      const anonymizedUser = getAnonymizedUserInfo(userRole, user, forceDepartment);
      
      const result = await updateDisputeStatus(selectedDisputeForDetails.id, {
        status: 'raised', // Change back to raised status
        userId: user.uid,
        userName: anonymizedUser.userName,
        department: anonymizedUser.department,
        message: `Query re-raised: ${reRaiseComment.trim()}`
      });

      if (result?.success) {
        loadDisputes();
        onDisputeUpdate && onDisputeUpdate();
        setShowReRaiseForm(false);
        setReRaiseComment('');
        setSelectedDisputeForDetails(null);
      }
    } catch (error) {
      console.error('Error re-raising query:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleIndividualQueryResponse = async (disputeId, queryIndex, responseData) => {
    setActionLoading(`individual-${disputeId}-${queryIndex}`);
    try {
      // Process the response data and handle file upload
      const processedResponse = { ...responseData };
      
      // NEW: Handle multiple file uploads (backward compatible with single fileResponse)
      const attachments = [];
      // Support new array form responseData.fileResponses
      const multiFiles = Array.isArray(responseData.fileResponses) ? responseData.fileResponses : [];
      const legacySingle = responseData.fileResponse instanceof File ? [responseData.fileResponse] : [];
      const filesToProcess = multiFiles.length > 0 ? multiFiles : legacySingle; // prefer new multi if present

      if (filesToProcess.length > 0) {
        for (const fileObj of filesToProcess) {
          if (!(fileObj instanceof File)) continue;
          try {
            console.log('Uploading file for individual query response:', fileObj.name);
            const filePath = generateDisputeFilePath(disputeId, queryIndex, fileObj.name);
            const uploadResult = await uploadFile(fileObj, filePath);
            if (uploadResult.success) {
              attachments.push({
                ...uploadResult.metadata,
                downloadURL: uploadResult.downloadURL,
                uploadStatus: 'uploaded'
              });
            } else {
              throw new Error('Upload failed');
            }
          } catch (uploadError) {
            console.error('Error uploading individual query file:', uploadError);
            attachments.push({
              fileName: fileObj.name,
              fileSize: fileObj.size,
              fileType: fileObj.type,
              lastModified: fileObj.lastModified,
              uploadStatus: 'failed',
              uploadError: uploadError.message
            });
            alert(`Warning: Failed to upload file "${fileObj.name}". Error: ${uploadError.message}`);
          }
        }
      }

      if (attachments.length > 0) {
        // Maintain backward compatibility (first file as fileMetadata) and new attachments array
        processedResponse.fileMetadata = attachments[0];
        processedResponse.attachments = attachments;
      } else {
        processedResponse.fileMetadata = null;
        processedResponse.attachments = [];
      }
      processedResponse.fileResponse = null;
      processedResponse.fileResponses = null;
      
      // Save the response locally
      const updatedForm = {
        ...responseForm,
        [`individual_response_${queryIndex}`]: {
          ...processedResponse,
          savedAt: new Date().toISOString(),
          status: 'saved'
        }
      };
      
      setResponseForm(updatedForm);
      
      // Show success feedback
      console.log('Individual query response saved:', processedResponse);
      
    } catch (error) {
      console.error('Error saving individual response:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDocumentQueryResponse = async (queryId, responseData) => {
    setActionLoading(`resolve-${queryId}`);
    setLoading(true);
    try {
      // For document queries, we need to handle both text replies and file uploads
      const textResponse = responseData.formData.issue_0_text || '';
      const filesArray = responseData.formData.issue_0_files || [];
      const hasSingleFile = responseData.formData.issue_0_file instanceof File;
      const fileToUpload = filesArray.length > 0 ? filesArray[0] : (hasSingleFile ? responseData.formData.issue_0_file : null);

      const anonymizedUser = getAnonymizedUserInfo(userRole, user);
      const repliedBy = {
        uid: user.uid,
        name: anonymizedUser.userName,
        email: user.email,
        team: 'hospital'
      };

      // If there's only a text response (no file), use replyToDocumentQuery
      if (!fileToUpload && textResponse.trim()) {
        const result = await replyToDocumentQuery(applicationId, queryId, textResponse, repliedBy);
        if (result.success) {
          alert('Reply sent successfully. The query remains open until you upload an updated document.');
          setShowResponseForm(false);
          setSelectedDispute(null);
          setResponseForm({});
          loadDocumentQueries();
          loadDisputes();
          onDisputeUpdate && onDisputeUpdate();
        } else {
          throw new Error(result.error || 'Failed to send reply');
        }
      }
      // If there's a file, use resolveDocumentQuery to upload and resolve
      else if (fileToUpload) {
        // First send any text as a reply
        if (textResponse.trim()) {
          await replyToDocumentQuery(applicationId, queryId, textResponse, repliedBy);
        }

        // Then upload the file and resolve
        console.log('Uploading updated document for query resolution:', fileToUpload.name);
        const documentKey = selectedDispute.documentKey;
        const filePath = `applications/${applicationId}/documents/${documentKey}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${fileToUpload.name}`;
        const uploadResult = await uploadFile(fileToUpload, filePath);
        
        if (!uploadResult.success) {
          throw new Error('Failed to upload document');
        }

        // Resolve the query with the new document
        const resolveResult = await resolveDocumentQuery(
          applicationId,
          queryId,
          uploadResult.downloadURL,
          fileToUpload.name,
          repliedBy
        );

        if (resolveResult.success) {
          alert('Document uploaded successfully! The query has been resolved and the document updated in the application.');
          setShowResponseForm(false);
          setSelectedDispute(null);
          setResponseForm({});
          loadDocumentQueries();
          loadDisputes();
          onDisputeUpdate && onDisputeUpdate();
        } else {
          throw new Error(resolveResult.error || 'Failed to resolve query');
        }
      } else {
        alert('Please provide either a text response or upload a document.');
      }
    } catch (error) {
      console.error('Error responding to document query:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
      setActionLoading(null);
    }
  };

  const handleResolveDispute = async (disputeId, responseData) => {
    setActionLoading(`resolve-${disputeId}`);
    setLoading(true);
    try {
      // Process individual query responses
      const processedFormData = { ...responseData.formData };
      
      // If we have issues, collect individual responses
      if (selectedDispute?.issues && selectedDispute.issues.length > 0) {
        processedFormData.individualResponses = await Promise.all(
          selectedDispute.issues.map(async (issue, index) => {
            const textKey = `issue_${index}_text`;
            const fileListKey = `issue_${index}_files`; // new multi-files key
            const legacyFileKey = `issue_${index}_file`; // backward compatibility
            
            const response = {
              queryIndex: index,
              queryTitle: issue.title || `Query #${index + 1}`,
              queryDescription: issue.description,
              queryType: issue.type,
              answerType: issue.answerType,
              textResponse: responseData.formData[textKey] || '',
              priority: issue.priority
            };

            // NEW multi-file upload support
            const filesArrayRaw = responseData.formData[fileListKey];
            const legacySingle = responseData.formData[legacyFileKey];
            const filesArray = Array.isArray(filesArrayRaw) && filesArrayRaw.length > 0
              ? filesArrayRaw
              : (legacySingle instanceof File ? [legacySingle] : []);
            const attachments = [];
            for (const fileObj of filesArray) {
              if (!(fileObj instanceof File)) continue;
              try {
                console.log('Uploading file to Firebase Storage for query', index, ':', fileObj.name);
                const filePath = generateDisputeFilePath(disputeId, index, fileObj.name);
                const uploadResult = await uploadFile(fileObj, filePath);
                if (uploadResult.success) {
                  attachments.push({
                    ...uploadResult.metadata,
                    downloadURL: uploadResult.downloadURL,
                    uploadStatus: 'uploaded'
                  });
                } else {
                  throw new Error('Upload failed');
                }
              } catch (uploadError) {
                console.error('Error uploading file for query', index, ':', uploadError);
                attachments.push({
                  fileName: fileObj.name,
                  fileSize: fileObj.size,
                  fileType: fileObj.type,
                  lastModified: fileObj.lastModified,
                  uploadStatus: 'failed',
                  uploadError: uploadError.message
                });
                alert(`Warning: Failed to upload file "${fileObj.name}" for Query #${index + 1}. Error: ${uploadError.message}`);
              }
            }
            if (attachments.length > 0) {
              response.fileMetadata = attachments[0]; // backward compatibility
              response.attachments = attachments;
            } else {
              response.fileMetadata = null;
              response.attachments = [];
            }

            return response;
          })
        );
        
        // Generate summary for resolution
        const answeredQueries = processedFormData.individualResponses.filter(resp => 
          resp.textResponse || resp.fileMetadata
        );
        
        if (answeredQueries.length === 0) {
          alert('Please provide responses for at least one query before submitting.');
          setLoading(false);
          setActionLoading(null);
          return;
        }
        
        // Update resolution with summary
        const summaryText = `Responded to ${answeredQueries.length} of ${selectedDispute.issues.length} queries. ${responseData.resolution || ''}`;
        processedFormData.resolution = summaryText.trim();
      }

      // Remove any remaining File objects from form data before sending to service
      const sanitizedFormData = {};
      Object.entries(processedFormData).forEach(([key, value]) => {
        // Skip File objects completely
        if (value instanceof File) {
          console.warn(`Removing File object from form data: ${key}`);
          return;
        }
        // Skip arrays containing File objects (raw multi-file inputs)
        if (Array.isArray(value) && value.some(v => v instanceof File)) {
          console.warn(`Removing File array from form data: ${key}`);
          return;
        }
        sanitizedFormData[key] = value;
      });

      const anonymizedUser = getAnonymizedUserInfo(userRole, user);
      
      const result = await resolveDisputeWithResponse(disputeId, {
        userId: user.uid,
        userName: anonymizedUser.userName, // Use anonymized name
        department: anonymizedUser.department,
        formData: sanitizedFormData, // Use sanitized data
        resolution: responseData.resolution
      });

      if (result.success) {
        setShowResponseForm(false);
        setSelectedDispute(null);
        setResponseForm({});
        loadDisputes();
        onDisputeUpdate && onDisputeUpdate();
      }
    } catch (error) {
      console.error('Error resolving dispute:', error);
    } finally {
      setLoading(false);
      setActionLoading(null);
    }
  };

  const getDisputeStatusColor = (status) => {
    const colors = {
      raised: 'bg-red-100 text-red-800',
      acknowledged: 'bg-yellow-100 text-yellow-800',
      under_review: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      escalated: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-green-600',
      medium: 'text-yellow-600',
      high: 'text-orange-600',
      urgent: 'text-red-600'
    };
    return colors[priority] || 'text-gray-600';
  };

  const disputeTypes = [
    { value: 'document_missing', label: 'Missing Documents', answerType: 'file' },
    { value: 'medical_clarification', label: 'Medical Clarification', answerType: 'text' },
    { value: 'policy_verification', label: 'Policy Verification', answerType: 'text' },
    { value: 'financial_review', label: 'Financial Review', answerType: 'text+file' },
    { value: 'kyc_incomplete', label: 'KYC Incomplete', answerType: 'file' },
    { value: 'other', label: 'Other', answerType: 'text' }
  ];

  const answerTypes = [
    { value: 'text', label: 'Text Only', icon: '📝' },
    { value: 'file', label: 'File Only', icon: '📎' },
    { value: 'text+file', label: 'Text + File', icon: '📝📎' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-start mb-6">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            Query Management
            {highlightDisputeId && (
              <span className="ml-2 text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded">
                📍 From Notification
              </span>
            )}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {(userRole === 'Doctor' || userRole === 'PolicyWriter' || userRole === 'FinancialTeam')
              ? "Raise queries or concerns about this application. Active disputes will block application processing until resolved."
              : "View and respond to disputes raised for this application."
            }
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Questionnaire Toggle for Hospital */}
          {userRole === 'Hospital' && onQuestionnaireToggle && (
            <button
              onClick={onQuestionnaireToggle}
              className={`px-4 py-2 rounded-md transition-colors flex items-center space-x-2 ${
                showQuestionnaire 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>{showQuestionnaire ? 'Hide Questionnaire' : 'Show Questionnaire'}</span>
            </button>
          )}

          {/* Raise Query Button */}
          {(userRole === 'Doctor' || userRole === 'PolicyWriter' || userRole === 'FinancialTeam') && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors flex items-center space-x-2 shadow-sm"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Raise Query</span>
            </button>
          )}
        </div>
      </div>

      {/* Active Query Alert */}
      {(disputes.some(d => ['raised', 'acknowledged', 'under_review'].includes(d.status)) || 
        documentQueries.some(q => q.status === 'open')) && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Active Query - Processing Blocked
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  This application has {disputes.filter(d => ['raised', 'acknowledged', 'under_review'].includes(d.status)).length} active dispute(s) 
                  {documentQueries.filter(q => q.status === 'open').length > 0 && ` and ${documentQueries.filter(q => q.status === 'open').length} document query(ies)`} that must be resolved before processing can continue.
                  {userRole === 'Hospital' && ' Please respond to the queries below.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unified Query List - Disputes + Document Queries */}
      <div className="space-y-4">
        {/* Combine and sort all queries */}
        {[...disputes, ...documentQueries]
          .sort((a, b) => {
            // Sort by raised date, newest first
            const dateA = a.raisedAt instanceof Date ? a.raisedAt : new Date(a.raisedAt);
            const dateB = b.raisedAt instanceof Date ? b.raisedAt : new Date(b.raisedAt);
            return dateB - dateA;
          })
          .map((dispute) => (
          <div 
            key={dispute.id} 
            className={`border rounded-lg p-4 transition-all ${
              highlightDisputeId === dispute.id 
                ? 'border-blue-500 bg-blue-50 shadow-md' 
                : 'border-gray-200'
            } ${dispute.isDocumentQuery ? 'border-l-4 border-l-purple-500' : ''}`}
          >
            {dispute.isDocumentQuery && (
              <div className="mb-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                📄 Document Query
              </div>
            )}
            {highlightDisputeId === dispute.id && (
              <div className="mb-3 text-sm text-blue-700 bg-blue-100 px-3 py-2 rounded-md">
                📍 <strong>From Notification:</strong> This dispute requires your attention
              </div>
            )}
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h4 className="text-md font-medium text-gray-900">{dispute.title}</h4>
                {dispute.description && (
                  <p className="text-sm text-gray-600 mt-1">{dispute.description}</p>
                )}
                
                {/* Display multiple issues if they exist */}
                {dispute.issues && dispute.issues.length > 0 ? (
                  <div className="mt-3">
                    <div className="text-xs font-medium text-gray-500 mb-2">
                      Issues ({dispute.issues.length}):
                    </div>
                    <div className="space-y-2">
                      {dispute.issues.map((issue, index) => (
                        <div key={index} className="bg-gray-50 rounded p-2 border-l-4 border-blue-500">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {issue.title || `Issue #${index + 1}`}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {disputeTypes.find(t => t.value === issue.type)?.label || issue.type}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">{issue.description}</div>
                              {issue.answerType && (
                                <div className="text-xs text-blue-600 mt-1 flex items-center">
                                  <span className="mr-1">
                                    {answerTypes.find(t => t.value === issue.answerType)?.icon || '📝'}
                                  </span>
                                  Requires: {answerTypes.find(t => t.value === issue.answerType)?.label || issue.answerType}
                                </div>
                              )}
                            </div>
                            <span className={`ml-2 text-xs font-medium ${getPriorityColor(issue.priority)}`}>
                              {issue.priority.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  // Fallback for old disputes without issues array
                  dispute.disputeType && (
                    <div className="mt-2 text-sm text-gray-600">
                      <span className="font-medium">Type:</span> {disputeTypes.find(t => t.value === dispute.disputeType)?.label || dispute.disputeType}
                    </div>
                  )
                )}
              </div>
              <div className="flex flex-col items-end space-y-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDisputeStatusColor(dispute.status)}`}>
                  {dispute.status.replace('_', ' ').toUpperCase()}
                </span>
                <span className={`text-xs font-medium ${getPriorityColor(dispute.priority)}`}>
                  {dispute.priority.toUpperCase()} PRIORITY
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
              <div>
                <span className="font-medium">Raised by:</span> {formatDisputeUserDisplay(dispute.raisedBy)}
              </div>
              <div>
                <span className="font-medium">Department:</span> {dispute.raisedBy.department}
              </div>
              <div>
                <span className="font-medium">Assigned to:</span> {dispute.assignedTo}
              </div>
              <div>
                <span className="font-medium">Created:</span> {dispute.raisedAt?.toLocaleDateString()}
              </div>
              {dispute.resolvedBy && (
                <>
                  <div>
                    <span className="font-medium">Responded by:</span> {dispute.resolvedBy.department === 'hospital' ? `${dispute.resolvedBy.userName} (Hospital)` : formatDisputeUserDisplay(dispute.resolvedBy)}
                  </div>
                  <div>
                    <span className="font-medium">Resolved on:</span> {dispute.resolvedAt?.toLocaleDateString()}
                  </div>
                </>
              )}
              {dispute.issues && dispute.issues.length > 0 && (
                <div className="col-span-2">
                  <span className="font-medium">Issues Count:</span> {dispute.issues.length} {dispute.issues.length === 1 ? 'issue' : 'issues'}
                </div>
              )}
            </div>

            {/* Required Fields for Resolution */}
            {dispute.requiredFields && dispute.requiredFields.length > 0 && (
              <div className="bg-gray-50 rounded-md p-3 mb-3">
                <h5 className="text-sm font-medium text-gray-900 mb-2">Required Information:</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  {dispute.requiredFields.map((field, index) => (
                    <li key={index} className="flex items-center">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-2">
              {dispute.status === 'raised' && userRole === 'Hospital' && (
                <button
                  onClick={() => {
                    setActionLoading(`respond-${dispute.id}`);
                    setSelectedDispute(dispute);
                    setShowResponseForm(true);
                    setActionLoading(null);
                  }}
                  disabled={actionLoading === `respond-${dispute.id}`}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                >
                  {actionLoading === `respond-${dispute.id}` ? (
                    <>
                      <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Opening...</span>
                    </>
                  ) : (
                    <span>📝 Respond to Query</span>
                  )}
                </button>
              )}
              
              {dispute.status === 'resolved' && 
               dispute.raisedBy.role === userRole && (
                <>
                  {dispute.accepted ? (
                    <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800 border border-green-200">Accepted</span>
                  ) : (
                    <>
                      <button
                        onClick={() => handleAcknowledgeDispute(dispute.id)}
                        disabled={actionLoading === `acknowledge-${dispute.id}`}
                        className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                      >
                        {actionLoading === `acknowledge-${dispute.id}` ? (
                          <>
                            <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Accepting...</span>
                          </>
                        ) : (
                          <span>✅ Accept Response</span>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedDisputeForDetails(dispute);
                          setShowReRaiseForm(true);
                        }}
                        className="bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700 transition-colors flex items-center space-x-1"
                      >
                        <span>🔄 Re-raise Query</span>
                      </button>
                    </>
                  )}
                </>
              )}

              <button
                onClick={() => {
                  setSelectedDisputeForDetails(dispute);
                  setShowDetailsModal(true);
                }}
                className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors flex items-center space-x-1"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span>View Details</span>
              </button>
            </div>

            {/* Communications Thread */}
            {dispute.communications && dispute.communications.length > 1 && (
              <div className="mt-4 border-t pt-3">
                <h6 className="text-sm font-medium text-gray-900 mb-2">Communication History:</h6>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {dispute.communications.slice(1).map((comm, index) => (
                    <div key={index} className="text-xs bg-gray-50 p-2 rounded">
                      <div className="font-medium text-gray-900">
                        {comm.fromDepartment === 'hospital' ? `${comm.fromName} (Hospital)` : formatDisputeUserDisplay({userName: comm.fromName, department: comm.fromDepartment})}
                      </div>
                      <div className="text-gray-600">{comm.message}</div>
                      <div className="text-gray-500 mt-1">
                        {comm.timestamp?.toDate?.()?.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {disputes.length === 0 && documentQueries.length === 0 && !loading && (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <div className="flex flex-col items-center">
              <svg className="h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No queries for this application</h3>
              <p className="text-gray-600 mb-4 max-w-sm">
                {(userRole === 'Doctor' || userRole === 'PolicyWriter' || userRole === 'FinancialTeam') 
                  ? "Start by raising disputes or document queries. Document queries can be raised from the Documents section."
                  : "Only doctors, policy writers, and financial team members can raise queries for applications."
                }
              </p>
              {(userRole === 'Doctor' || userRole === 'PolicyWriter' || userRole === 'FinancialTeam') && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors flex items-center space-x-2"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Raise New Dispute</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Query Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Raise New Query</h3>
            <form onSubmit={handleCreateDispute}>
              <div className="space-y-4">
                {/* Optional Overall Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Overall Title (Optional)
                  </label>
                  <input
                    type="text"
                    value={disputeForm.title}
                    onChange={(e) => setDisputeForm({...disputeForm, title: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Optional: Brief title for this dispute"
                  />
                </div>

                {/* Issues Section - Main Focus */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Issues/Complaints ({disputeForm.issues.length})
                    </label>
                    <button
                      type="button"
                      onClick={addIssue}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors flex items-center space-x-1"
                    >
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span>Add Issue</span>
                    </button>
                  </div>

                  <div className="space-y-4">
                    {disputeForm.issues.map((issue, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="text-sm font-medium text-gray-700">Issue #{index + 1}</h4>
                          {disputeForm.issues.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeIssue(index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        {/* Standard Query Search for Each Issue */}
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            📋 Search Standard Query for this Issue
                            {localQueriesLoaded && (
                              <span className="ml-2 text-green-600 text-xs">
                                (🟢 Local Queries Loaded)
                              </span>
                            )}
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              placeholder={localQueriesLoaded ? "Type to search 393+ predefined queries..." : "Type to search predefined queries..."}
                              className="w-full p-2 border border-blue-200 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                              onChange={(e) => {
                                const value = e.target.value;
                                searchStandardQueries(value);
                                // Store the current issue index for selection
                                setDisputeForm(prev => ({...prev, _currentIssueIndex: index}));
                              }}
                            />
                            
                            {/* Standard Queries Dropdown for Individual Issue */}
                            {showAutocomplete && (
                              <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 max-h-48 overflow-y-auto">
                                {autocompleteLoading ? (
                                  <div className="p-3 text-center text-gray-500 text-sm">
                                    🔍 Searching local queries...
                                  </div>
                                ) : filteredQueries.length > 0 ? (
                                  <>
                                    <div className="px-3 py-2 bg-green-50 border-b text-xs text-green-700">
                                      📋 {filteredQueries.length} local queries found
                                    </div>
                                    {filteredQueries.map((query, qIndex) => (
                                      <div
                                        key={qIndex}
                                        className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                        onClick={() => selectStandardQuery(query, index)}
                                      >
                                        <div className="font-medium text-sm text-gray-900">
                                          {query.queryHeader || 'Standard Query'}
                                        </div>
                                        <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                                          {query.queryDescription || query.documentDescription}
                                        </div>
                                        {query.queryType && (
                                          <div className="text-xs text-blue-600 mt-1">
                                            Type: {query.queryType}
                                          </div>
                                        )}
                                        {query.requirementStages && query.requirementStages.length > 0 && (
                                          <div className="text-xs text-purple-600 mt-1">
                                            Stages: {query.requirementStages.join(', ')}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </>
                                ) : (
                                  <div className="p-3 text-center text-gray-500 text-sm">
                                    🚫 No local queries match your search
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Issue Title */}
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Issue Title *
                          </label>
                          <input
                            type="text"
                            value={issue.title}
                            onChange={(e) => updateIssue(index, 'title', e.target.value)}
                            className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Title for this specific issue"
                            required
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Issue Type *
                            </label>
                            <select
                              value={issue.type}
                              onChange={(e) => {
                                const selectedValue = e.target.value;
                                
                                // Find the selected type and get its default answer type
                                const selectedType = disputeTypes.find(t => t.value === selectedValue);
                                const defaultAnswerType = selectedType?.answerType || 'text';
                                
                                // Update both type and answerType in a single call
                                const newIssues = disputeForm.issues.map((currentIssue, i) => {
                                  if (i === index) {
                                    return {
                                      ...currentIssue,
                                      type: selectedValue,
                                      answerType: defaultAnswerType
                                    };
                                  }
                                  return currentIssue;
                                });
                                
                                setDisputeForm({ ...disputeForm, issues: newIssues });
                              }}
                              className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              required
                            >
                              <option value="">Select type</option>
                              {disputeTypes.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Priority
                            </label>
                            <select
                              value={issue.priority}
                              onChange={(e) => updateIssue(index, 'priority', e.target.value)}
                              className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="urgent">Urgent</option>
                            </select>
                          </div>
                        </div>

                        {/* Answer Type Selection */}
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Required Answer Type *
                          </label>
                          <div className="grid grid-cols-3 gap-2">
                            {answerTypes.map(type => {
                              const isSelected = issue.answerType === type.value;
                              return (
                                <button
                                  key={type.value}
                                  type="button"
                                  onClick={() => updateIssue(index, 'answerType', type.value)}
                                  className={`p-2 rounded-md border text-xs font-medium transition-colors ${
                                    isSelected
                                      ? 'bg-blue-100 border-blue-500 text-blue-700'
                                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                                  }`}
                                >
                                  <div className="text-center">
                                    <div className="text-sm mb-1">{type.icon}</div>
                                    <div>{type.label}</div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Issue Description */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Description *
                          </label>
                          <textarea
                            value={issue.description}
                            onChange={(e) => updateIssue(index, 'description', e.target.value)}
                            rows="2"
                            className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Describe this specific issue in detail..."
                            required
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Overall Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Overall Priority
                    </label>
                    <select
                      value={disputeForm.priority}
                      onChange={(e) => setDisputeForm({...disputeForm, priority: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assign to
                    </label>
                    <select
                      value={disputeForm.assignedTo}
                      onChange={(e) => setDisputeForm({...disputeForm, assignedTo: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="hospital">Hospital</option>
                      <option value="medical">Medical Department</option>
                      <option value="policy">Policy Department</option>
                      <option value="financial">Financial Department</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : `Raise Query (${disputeForm.issues.length} ${disputeForm.issues.length === 1 ? 'Issue' : 'Issues'})`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Response Form Modal */}
      {showResponseForm && selectedDispute && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {selectedDispute.isDocumentQuery ? '📄 Respond to Document Query' : 'Respond to Query'}
            </h3>
            
            <div className={`p-3 rounded-md mb-4 ${
              selectedDispute.isDocumentQuery ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50'
            }`}>
              <h4 className="font-medium text-gray-900">{selectedDispute.title}</h4>
              <p className="text-sm text-gray-600 mt-1">{selectedDispute.description}</p>
              <div className="text-xs mt-2 ${
                selectedDispute.isDocumentQuery ? 'text-purple-600' : 'text-blue-600'
              }">
                {selectedDispute.isDocumentQuery 
                  ? '📄 You can reply with text only, or upload an updated document to resolve the query. Uploading a document will automatically update it in the application.'
                  : '📝 Respond to each query below based on the required answer type'
                }
              </div>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              // Route to appropriate handler based on query type
              if (selectedDispute.isDocumentQuery) {
                handleDocumentQueryResponse(selectedDispute.id, {
                  formData: responseForm,
                  resolution: responseForm.resolution || 'Document query addressed'
                });
              } else {
                handleResolveDispute(selectedDispute.id, {
                  formData: responseForm,
                  resolution: responseForm.resolution || 'All queries resolved with provided information'
                });
              }
            }}>
              <div className="space-y-6">
                {/* Individual Query Responses */}
                {selectedDispute.issues && selectedDispute.issues.length > 0 ? (
                  <div>
                    <h5 className="text-md font-medium text-gray-900 mb-4">
                      Individual Query Responses ({selectedDispute.issues.length} queries)
                    </h5>
                    <div className="space-y-4">
                      {selectedDispute.issues.map((issue, index) => {
                        const responseKey = `issue_${index}`;
                        const textKey = `${responseKey}_text`;
                        const fileKey = `${responseKey}_file`; // legacy single file key
                        const filesKey = `${responseKey}_files`; // new multi-file key
                        
                        return (
                          <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <h6 className="font-medium text-gray-900">
                                  Query #{index + 1}: {issue.title || 'Untitled Query'}
                                </h6>
                                <p className="text-sm text-gray-600 mt-1">{issue.description}</p>
                                <div className="text-xs text-gray-500 mt-1">
                                  Type: {disputeTypes.find(t => t.value === issue.type)?.label || issue.type}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded flex items-center">
                                  {answerTypes.find(t => t.value === issue.answerType)?.icon || '📝'}
                                  <span className="ml-1">
                                    {answerTypes.find(t => t.value === issue.answerType)?.label || issue.answerType}
                                  </span>
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Prepare individual response data
                                      const fileObjs = responseForm[filesKey];
                                      const fileObjLegacy = responseForm[fileKey];
                                    const individualResponse = {
                                      queryIndex: index,
                                      queryTitle: issue.title,
                                      queryDescription: issue.description,
                                      queryType: issue.type,
                                      answerType: issue.answerType,
                                      textResponse: responseForm[textKey] || '',
                                        // Pass multi or single for processing
                                        fileResponses: Array.isArray(fileObjs) && fileObjs.length > 0 ? fileObjs : (fileObjLegacy instanceof File ? [fileObjLegacy] : []),
                                        fileResponse: fileObjLegacy instanceof File ? fileObjLegacy : null, // backward compat
                                      priority: issue.priority
                                    };
                                    
                                    // Validate response based on answer type
                                    let isValid = false;
                                    if (issue.answerType === 'text' && individualResponse.textResponse.trim()) {
                                      isValid = true;
                                    } else if (issue.answerType === 'file' && individualResponse.fileResponses && individualResponse.fileResponses.length > 0) {
                                      isValid = true;
                                    } else if (issue.answerType === 'text+file' && (individualResponse.textResponse.trim() || (individualResponse.fileResponses && individualResponse.fileResponses.length > 0))) {
                                      isValid = true;
                                    }
                                    
                                    if (!isValid) {
                                      alert(`Please provide a ${issue.answerType.replace('+', ' and/or ')} response for this query.`);
                                      return;
                                    }
                                    
                                    // Save individual response
                                    handleIndividualQueryResponse(selectedDispute.id, index, individualResponse);
                                  }}
                                  disabled={actionLoading === `individual-${selectedDispute.id}-${index}`}
                                  className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                                >
                                  {actionLoading === `individual-${selectedDispute.id}-${index}` ? (
                                    <>
                                      <svg className="animate-spin h-2 w-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      <span>Saving...</span>
                                    </>
                                  ) : (
                                    <>
                                      <span>✓</span>
                                      <span>Save This Response</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>

                            {/* Response fields based on answerType */}
                            <div className="space-y-3">
                              {/* Text response (for text and text+file types) */}
                              {(issue.answerType === 'text' || issue.answerType === 'text+file') && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Text Response *
                                  </label>
                                  <textarea
                                    value={responseForm[textKey] || ''}
                                    onChange={(e) => setResponseForm({
                                      ...responseForm,
                                      [textKey]: e.target.value
                                    })}
                                    rows="3"
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    placeholder={`Provide detailed response for: ${issue.title}`}
                                    required
                                  />
                                </div>
                              )}

                              {/* File upload (for file and text+file types) */}
                              {(issue.answerType === 'file' || issue.answerType === 'text+file') && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    File Upload {issue.answerType === 'file' ? '*' : '(Optional)'}
                                  </label>
                                  <div className="border-2 border-dashed border-gray-300 rounded-md p-4 hover:border-blue-400 transition-colors">
                                    <input
                                      type="file"
                                      multiple
                                      onChange={(e) => {
                                        const files = Array.from(e.target.files || []);
                                        setResponseForm({
                                          ...responseForm,
                                          [filesKey]: files,
                                          [fileKey]: files.length === 1 ? files[0] : null // maintain single for backward paths
                                        });
                                      }}
                                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                                      required={issue.answerType === 'file'}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                      Supported: PDF, DOC, DOCX, JPG, PNG, TXT (Max 100MB each). You can select multiple files.
                                    </p>
                                    {(responseForm[filesKey] && responseForm[filesKey].length > 0) && (
                                      <div className="mt-2 space-y-2">
                                        {responseForm[filesKey].map((f, i) => (
                                          <div key={i} className="p-2 bg-green-50 border border-green-200 rounded flex justify-between items-start">
                                            <div className="text-xs text-green-700">
                                              <div className="font-medium">✓ {f.name}</div>
                                              <div className="text-green-600">Size: {formatFileSize(f.size)} | Type: {f.type || 'n/a'}</div>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const remaining = responseForm[filesKey].filter((_, idx) => idx !== i);
                                                setResponseForm({
                                                  ...responseForm,
                                                  [filesKey]: remaining,
                                                  [fileKey]: remaining.length === 1 ? remaining[0] : null
                                                });
                                              }}
                                              className="text-red-600 hover:text-red-800 text-xs ml-2"
                                            >
                                              ✗ Remove
                                            </button>
                                          </div>
                                        ))}
                                        <div className="text-xs text-gray-500">Total files: {responseForm[filesKey].length}</div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Progress indicator */}
                              <div className="flex items-center justify-between text-xs text-gray-500">
                                <div>
                                  Status: {
                                    responseForm[`individual_response_${index}`]?.status === 'saved' 
                                      ? <span className="text-green-600">✓ Response saved locally</span>
                                      : (issue.answerType === 'text' && responseForm[textKey]) ||
                                        (issue.answerType === 'file' && responseForm[fileKey]) ||
                                        (issue.answerType === 'text+file' && (responseForm[textKey] || responseForm[fileKey]))
                                        ? <span className="text-blue-600">📝 Response ready</span>
                                        : <span className="text-orange-600">⏳ Awaiting response</span>
                                  }
                                </div>
                                <div>
                                  Priority: <span className={`font-medium ${getPriorityColor(issue.priority)}`}>
                                    {issue.priority.toUpperCase()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  /* Fallback for legacy disputes without issues */
                  <div className="space-y-4">
                    {selectedDispute.requiredFields?.map((field, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {field.type === 'textarea' ? (
                          <textarea
                            value={responseForm[field.name] || ''}
                            onChange={(e) => setResponseForm({
                              ...responseForm,
                              [field.name]: e.target.value
                            })}
                            rows="3"
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            required={field.required}
                          />
                        ) : (
                          <input
                            type={field.type || 'text'}
                            value={responseForm[field.name] || ''}
                            onChange={(e) => setResponseForm({
                              ...responseForm,
                              [field.name]: e.target.value
                            })}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            required={field.required}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Overall Additional Comments */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Comments (Optional)
                  </label>
                  <textarea
                    value={responseForm.resolution || ''}
                    onChange={(e) => setResponseForm({
                      ...responseForm,
                      resolution: e.target.value
                    })}
                    rows="3"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Any additional information or general comments about all queries..."
                  />
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t">
                <div className="text-sm text-gray-600">
                  {selectedDispute.issues ? (
                    <>
                      Progress: {selectedDispute.issues.filter((issue, index) => {
                        const textKey = `issue_${index}_text`;
                        const fileKey = `issue_${index}_file`;
                                const filesKey = `issue_${index}_files`;
                                return (issue.answerType === 'text' && responseForm[textKey]) ||
                               (issue.answerType === 'file' && responseForm[filesKey] && responseForm[filesKey].length > 0) ||
                               (issue.answerType === 'text+file' && (responseForm[textKey] || (responseForm[filesKey] && responseForm[filesKey].length > 0)));
                      }).length} of {selectedDispute.issues.length} queries answered
                    </>
                  ) : (
                    'Legacy dispute format'
                  )}
                </div>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResponseForm(false);
                      setSelectedDispute(null);
                      setResponseForm({});
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || actionLoading?.startsWith('resolve-')}
                    className={`px-6 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 ${
                      selectedDispute.isDocumentQuery ? 'bg-purple-600' : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {(loading || actionLoading?.startsWith('resolve-')) ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>
                          {selectedDispute.isDocumentQuery 
                            ? '📄 Submit Response (Upload updates document)' 
                            : 'Submit All Responses'
                          }
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showDetailsModal && selectedDisputeForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Query Details</h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedDisputeForDetails(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Query Overview */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Query Overview</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Title:</span> {selectedDisputeForDetails.title}
                  </div>
                  <div>
                    <span className="font-medium">Status:</span> 
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getDisputeStatusColor(selectedDisputeForDetails.status)}`}>
                      {selectedDisputeForDetails.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Priority:</span> 
                    <span className={`ml-2 font-medium ${getPriorityColor(selectedDisputeForDetails.priority)}`}>
                      {selectedDisputeForDetails.priority.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Created:</span> {selectedDisputeForDetails.raisedAt?.toLocaleDateString()}
                  </div>
                  <div>
                    <span className="font-medium">Raised by:</span> {formatDisputeUserDisplay(selectedDisputeForDetails.raisedBy)}
                  </div>
                  <div>
                    <span className="font-medium">Assigned to:</span> {selectedDisputeForDetails.assignedTo}
                  </div>
                </div>
              </div>

              {/* Issues Details */}
              {selectedDisputeForDetails.issues && selectedDisputeForDetails.issues.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Issues ({selectedDisputeForDetails.issues.length})</h4>
                  <div className="space-y-3">
                    {selectedDisputeForDetails.issues.map((issue, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex justify-between items-start mb-2">
                          <h5 className="font-medium text-gray-900">{issue.title || `Issue #${index + 1}`}</h5>
                          <span className={`text-xs font-medium ${getPriorityColor(issue.priority)}`}>
                            {issue.priority.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">Type:</span> {disputeTypes.find(t => t.value === issue.type)?.label || issue.type}
                        </div>
                        <div className="text-sm text-gray-700 mb-2">{issue.description}</div>
                        {issue.answerType && (
                          <div className="text-xs text-blue-600 flex items-center">
                            <span className="mr-1">
                              {answerTypes.find(t => t.value === issue.answerType)?.icon || '📝'}
                            </span>
                            Required: {answerTypes.find(t => t.value === issue.answerType)?.label || issue.answerType}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hospital Response */}
              {selectedDisputeForDetails.status === 'resolved' && selectedDisputeForDetails.resolverResponse && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Hospital Response</h4>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <svg className="h-5 w-5 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-medium text-green-800">
                        Responded by: {selectedDisputeForDetails.resolvedBy?.userName} (Hospital)
                      </span>
                    </div>
                    <div className="text-sm text-green-700 mb-2">
                      <span className="font-medium">Response Date:</span> {selectedDisputeForDetails.resolvedAt?.toLocaleDateString()}
                    </div>
                    <div className="text-sm text-gray-700">
                      <div className="font-medium mb-3">Response Details:</div>
                      
                      {/* Display individual query responses if available */}
                      {selectedDisputeForDetails.resolverResponse.individualResponses ? (
                        <div className="space-y-4">
                          {selectedDisputeForDetails.resolverResponse.individualResponses.map((response, index) => (
                            <div key={index} className="border border-gray-200 rounded p-3 bg-gray-50">
                              <div className="font-medium text-gray-900 mb-2">
                                Query #{response.queryIndex + 1}: {response.queryTitle}
                              </div>
                              
                              {response.textResponse && (
                                <div className="mb-2">
                                  <div className="text-xs font-medium text-gray-600 mb-1">Text Response:</div>
                                  <div className="text-sm text-gray-700 bg-white p-2 rounded border">
                                    {response.textResponse}
                                  </div>
                                </div>
                              )}
                              
                              {/* Display single or multiple attachments */}
                              {response.attachments && response.attachments.length > 0 ? (
                                <div className="mb-2">
                                  <div className="text-xs font-medium text-gray-600 mb-1">Attachments ({response.attachments.length}):</div>
                                  {response.attachments.length === 1 ? (
                                    <FileViewer fileMetadata={response.attachments[0]} />
                                  ) : (
                                    <FileList title="Attachments" files={response.attachments} />
                                  )}
                                </div>
                              ) : response.fileMetadata ? (
                                <div className="mb-2">
                                  <div className="text-xs font-medium text-gray-600 mb-1">Attachment:</div>
                                  <FileViewer fileMetadata={response.fileMetadata} />
                                </div>
                              ) : null}
                              
                              <div className="text-xs text-gray-500">
                                Query Type: {response.queryType} | Priority: {response.priority} | Answer Type: {response.answerType}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        // Legacy format - display as key-value pairs (+basic file list if present)
                        <div>
                          {Object.entries(selectedDisputeForDetails.resolverResponse).map(([key, value]) => {
                            // Skip individual responses as they're handled above
                            if (key === 'individualResponses') return null;
                            
                            return (
                              <div key={key} className="mb-1">
                                <span className="font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span> {value}
                              </div>
                            );
                          })}
                          {/* Try to render a generic attachments array if present */}
                          {Array.isArray(selectedDisputeForDetails.resolverResponse.attachments) && selectedDisputeForDetails.resolverResponse.attachments.length > 0 && (
                            <div className="mt-3">
                              <FileList title="Attachments" files={selectedDisputeForDetails.resolverResponse.attachments} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {selectedDisputeForDetails.resolution && (
                      <div className="mt-3 text-sm text-gray-700">
                        <div className="font-medium">Additional Comments:</div>
                        <div className="bg-white p-2 rounded border">{selectedDisputeForDetails.resolution}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Communication History */}
              {selectedDisputeForDetails.communications && selectedDisputeForDetails.communications.length > 1 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Communication History</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedDisputeForDetails.communications.map((comm, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded border">
                        <div className="flex justify-between items-start mb-1">
                          <div className="font-medium text-gray-900">
                            {comm.fromDepartment === 'hospital' ? `${comm.fromName} (Hospital)` : formatDisputeUserDisplay({userName: comm.fromName, department: comm.fromDepartment})}
                          </div>
                          <div className="text-xs text-gray-500">
                            {comm.timestamp?.toDate?.()?.toLocaleString()}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">{comm.message}</div>
                        {comm.type && (
                          <div className="text-xs text-blue-600 mt-1">
                            Type: {comm.type}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedDisputeForDetails(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-raise Query Modal */}
      {showReRaiseForm && selectedDisputeForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Re-raise Query</h3>
              <button
                onClick={() => {
                  setShowReRaiseForm(false);
                  setReRaiseComment('');
                  setSelectedDisputeForDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Query:</strong> {selectedDisputeForDetails.question}
              </p>
              {selectedDisputeForDetails.responses && selectedDisputeForDetails.responses.length > 0 && (
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Hospital Response:</strong> {selectedDisputeForDetails.responses[selectedDisputeForDetails.responses.length - 1].message}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="reRaiseComment" className="block text-sm font-medium text-gray-700 mb-2">
                Reason for re-raising query *
              </label>
              <textarea
                id="reRaiseComment"
                value={reRaiseComment}
                onChange={(e) => setReRaiseComment(e.target.value)}
                placeholder="Please explain why the hospital's response is not satisfactory..."
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                required
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowReRaiseForm(false);
                  setReRaiseComment('');
                  setSelectedDisputeForDetails(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleReRaiseQuery}
                disabled={!reRaiseComment.trim() || actionLoading === `re-raise-${selectedDisputeForDetails.id}`}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {actionLoading === `re-raise-${selectedDisputeForDetails.id}` ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Re-raising...</span>
                  </>
                ) : (
                  <span>🔄 Re-raise Query</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisputeManager;
